import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {boxedInputColorReaders} from '../../../../../../../../shared/ui/src/components/boxedInputs/boxedInputConstants';
import {TextBox} from '../../../../../../../../shared/ui/src/components/boxedInputs/textBox';

/*
 * Constants.
 */

const https = 'https://';

export enum MicrosoftDynamicsErrorsEnum {
  INVALID_URL = 'INVALID_URL',
  REQUIRED = 'REQUIRED',
}

const MicrosoftDynamicsErrorsEnumToDisplayText: {[key in MicrosoftDynamicsErrorsEnum]: ReactNode} = {
  [MicrosoftDynamicsErrorsEnum.INVALID_URL]: (
    <Trans id="crm-sync-page-microsoft-dynamics-environment-input-invalid-url-error">
      Please enter a valid URL
    </Trans>
  ),
  [MicrosoftDynamicsErrorsEnum.REQUIRED]: (
    <Trans id="crm-sync-page-microsoft-dynamics-environment-input-required-error">
      This field is required
    </Trans>
  ),
};

/*
 * Props.
 */

interface MicrosoftDynamicsUrlProps {
  url: string;
  isDisabled: boolean;
  error?: MicrosoftDynamicsErrorsEnum;
  onChange: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledTextBoxWrapperDiv = styled.div`
  position: relative;
  width: 430px;
  margin-bottom: 4px;
`;

interface StyledHttpsValueDivProps {
  $isDisabled: boolean;
}

const StyledHttpsValueDiv = styled.div<StyledHttpsValueDivProps>`
  position: absolute;
  top: 30px;
  left: 8px;
  font-size: 14px;
  color: ${(p) => p.theme.greys.shade90};
  // disabled styles from from boxedInput.tsx
  ${(p) =>
    p.$isDisabled &&
    css`
      opacity: 0.5;
      color: ${boxedInputColorReaders.textDisabled};
      user-select: none;
      cursor: not-allowed;
    `}
`;

const StyledTextBox = styled(TextBox)`
  input {
    margin-left: 55px;
  }
`;

/*
 * Component.
 */

export const MicrosoftDynamicsUrl: FC<MicrosoftDynamicsUrlProps> = (props) => {
  const {url, isDisabled, error, onChange} = props;

  // We want the https:// part to always be visible, but also want to have placeholder text
  // We use absolute positioning to keep the https:// part in the right place
  // Margins keep the placeholder and input area to the right of https://
  // Using :before or :after elements on the input would be a bit safer, but using :before or :after does not work on inputs
  return (
    <StyledTextBoxWrapperDiv>
      <StyledTextBox
        label={
          <Trans id="crm-sync-page-microsoft-dynamics-environment-input-label">Your Dynamics 365 URL</Trans>
        }
        placeholder="subdomain.crm.dynamics.com"
        hasError={error !== undefined}
        errorMessage={error !== undefined ? MicrosoftDynamicsErrorsEnumToDisplayText[error] : null}
        value={url}
        isDisabled={isDisabled}
        onChange={(newUrl: string) => onUrlChange(newUrl, onChange)}
      />
      <StyledHttpsValueDiv $isDisabled={isDisabled}>{https}</StyledHttpsValueDiv>
    </StyledTextBoxWrapperDiv>
  );
};

/*
 * Helpers.
 */

// exported for unit test
export function onUrlChange(newUrl: string, onChange: HandlerOf<string>) {
  // we try our best to format what they input into a valid dynamics environment url
  try {
    // if they input a valid url we want to strip everything out except the hostname
    // the hostname is the only part we need, we do not care about the path as this is used for the base api url
    // we need to readd https to construct the url object (if we previously removed it)
    let urlWithHttps = newUrl;
    // only add if we don't have http in there
    // otherwise leads to weird behavior if the user is manually typing in their url
    if (!urlWithHttps.startsWith(https) && !urlWithHttps.includes('http')) {
      urlWithHttps = `${https}${newUrl}`;
    }
    const updatedUrl = new URL(urlWithHttps);
    onChange(updatedUrl.hostname);
  } catch (e) {
    // if we fail to construct a URL object just call on change with the input so user sees what they typed
    onChange(newUrl);
  }
}

/**
 * Takes a string like front.crm.dynamics.com
 * and returns a string like https://front.api.crm.dynamics.com
 * This is the base api url that the backend expects
 */
export function formatMicrosoftDynamicsUrl(url: string): string {
  // .replace will replace first . with .api.
  return `${https}${url.replace('.', '.api.')}`;
}

/**
 * Takes a string like https://front.api.crm.dynamics.com
 * and returns a string like front.crm.dynamics.com
 *
 * We do not want to show the api part and we already have a https:// placeholder
 */
export function formatExistingMicrosoftDynamicsUrl(url: string | undefined): string {
  return url ? url.replace(https, '').replace('.api', '') : '';
}

/**
 * Checks if a url is a valid Microsoft Dynamics url.
 * Should match check in backend in server/integrations/microsoftdynamics/server/index.ts
 * https://front.api.crm.dynamics.com or https://front.api.crm5.dynamics.com are examples of valid urls.
 */
export function isMicrosoftDynamicsEnvironmentUrlValid(url: string): MicrosoftDynamicsErrorsEnum | undefined {
  if (url === '') {
    return MicrosoftDynamicsErrorsEnum.REQUIRED;
  }

  try {
    const urlFormatted = new URL(formatMicrosoftDynamicsUrl(url));
    if (urlFormatted.pathname !== '/') {
      // make sure there is no path in the url
      return MicrosoftDynamicsErrorsEnum.INVALID_URL;
    }
    const urlRegex = /^https:\/\/[a-zA-Z0-9]*\.api\.crm(.{0}|.*)\.dynamics.com$/;
    const matches = urlRegex.exec(urlFormatted.origin);
    if (!matches) {
      return MicrosoftDynamicsErrorsEnum.INVALID_URL;
    }
  } catch {
    return MicrosoftDynamicsErrorsEnum.INVALID_URL;
  }
  return undefined;
}
